/**
 * BankItemHistoryDialog — view prior versions of a bank item.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { History, CheckCircle2, Loader2 } from 'lucide-react';

export default function BankItemHistoryDialog({ open, onClose, bankId, item }) {
  const { get } = useApi();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setLoading(true);
    get(`/question-banks/${bankId}/items/${item.item_id}/versions`)
      .then(res => setVersions(res.items || []))
      .catch(e => toast.error(e.response?.data?.detail || 'Failed to load history'))
      .finally(() => setLoading(false));
  }, [open, item, bankId, get]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <History className="w-4 h-4" />Version history
          </DialogTitle>
          <DialogDescription>
            Every edit creates a new immutable version. Older versions remain auditable.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…
          </div>
        ) : versions.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No history yet.</p>
        ) : (
          <div className="space-y-3 py-2">
            {versions.map(v => (
              <div
                key={`${v.item_id}-${v.version}`}
                className={`rounded-lg border p-3 space-y-2 ${v.is_current ? 'border-[var(--lumyn-banks)] bg-[var(--lumyn-banks)]/5' : 'bg-card'}`}
                data-testid={`history-version-${v.version}`}
              >
                <div className="flex items-center justify-between text-xs">
                  <span className="font-mono font-semibold">
                    v{v.version}
                    {v.is_current && <span className="ml-2 text-[10px] uppercase text-[var(--lumyn-banks)]">current</span>}
                  </span>
                  <span className="text-muted-foreground">
                    {v.updated_at ? new Date(v.updated_at).toLocaleString() : ''}
                  </span>
                </div>
                <p className="text-sm font-medium">{v.question_text}</p>
                {v.options?.length > 0 && (
                  <div className="space-y-1">
                    {v.options.map((o, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${o.is_correct ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'}`}
                      >
                        {o.is_correct && <CheckCircle2 className="w-3 h-3" />}
                        {o.text}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                  <span>{v.question_type}</span>
                  {v.difficulty && <span>· {v.difficulty}</span>}
                  {v.bloom_level && <span>· {v.bloom_level}</span>}
                  {v.language && <span>· {v.language}</span>}
                  <span>· status {v.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="history-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
